import React from 'react';

import { ActionIcon, Box, Flex, Group, Text, ThemeIcon, Tooltip, useMantineTheme } from '@mantine/core';
import { IconCircleCheck, IconDotsVertical, IconPlus } from '@tabler/icons-react';
import useWorkoutAssignment from 'hooks/workout/useWorkoutAssignment';

import AssignWorkoutModal from '../../../../../AssignWorkoutModal';
import DeleteWorkoutModal from '../../../../../DeleteWorkoutModal';
import WorkoutAssignmentMenu from '../../../../../WorkoutAssignmentMenu';
import WorkoutVisibilityIcon from '../../../../../WorkoutVisibilityIcon';
import { WorkoutAssignment } from '../../../../interface';

interface WorkoutAssignedCellProps {
  assignment: WorkoutAssignment | null;
  kiloClassTypeId: string;
  kiloClassTypeName: string;
  date: string;
  onPreviewOpen: () => void;
}

const WorkoutAssignedCell = ({
  assignment,
  kiloClassTypeId,
  kiloClassTypeName,
  date,
  onPreviewOpen,
}: WorkoutAssignedCellProps): JSX.Element => {
  const theme = useMantineTheme();
  const workoutAssignment = useWorkoutAssignment({
    kiloClassTypeId,
    kiloClassTypeName,
    date,
    workoutId: assignment?.workoutId,
    workoutTemplateId: assignment?.workoutTemplate?.workoutTemplateId,
    isVisibleToAthletes: assignment?.isVisibleToAthletes,
    view: 'agenda',
  });
  const displayWorkoutName = assignment?.workoutTemplate?.name?.trim() || 'Assigned';

  return (
    <>
      {!assignment ? (
        <Tooltip
          label={workoutAssignment.assignBlockedLabel}
          disabled={!workoutAssignment.isAssignBlocked}
          withArrow
          position='top'
        >
          <Box
            onClick={e => {
              if (workoutAssignment.isAssignBlocked) return;
              e.stopPropagation();
              workoutAssignment.openAssignModal();
            }}
            sx={{
              flex: 1,
              borderRadius: theme.radius.md,
              border: `1.5px dashed ${theme.colors.gray[4]}`,
              cursor: workoutAssignment.isAssignBlocked ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: workoutAssignment.isAssignBlocked ? theme.colors.gray[4] : theme.colors.gray[6],
              transition: 'border-color 150ms ease, color 150ms ease',
              '&:hover': workoutAssignment.isAssignBlocked
                ? {}
                : {
                    borderColor: theme.colors.kiloBlue[6],
                    color: theme.colors.kiloBlue[6],
                  },
            }}
          >
            <Group spacing={6} noWrap>
              <IconPlus size={14} />
              <Text fw={600} size='sm'>
                Assign Workout
              </Text>
            </Group>
          </Box>
        </Tooltip>
      ) : (
        <Flex
          align='center'
          justify='space-between'
          px='sm'
          py='xs'
          style={{
            flex: 1,
            borderRadius: theme.radius.md,
            border: `1px solid ${theme.colors.green[3]}`,
            overflow: 'hidden',
            cursor: 'pointer',
          }}
          bg={theme.colors.green[0]}
          onClick={e => {
            e.stopPropagation();
            onPreviewOpen();
          }}
        >
          <Group spacing='xs' noWrap>
            <ThemeIcon color='green' variant='light' radius='xl' size='sm' sx={{ flexShrink: 0 }}>
              <IconCircleCheck size={16} />
            </ThemeIcon>
            <Text color='green.7' fw={600} size='sm' lineClamp={1}>
              {displayWorkoutName}
            </Text>
            <WorkoutVisibilityIcon
              visible={assignment.isVisibleToAthletes}
              visibleColor={theme.colors.green[7]}
            />
          </Group>
          <WorkoutAssignmentMenu
            canMutate={workoutAssignment.canMutate}
            hasWorkoutId={!!assignment.workoutId}
            owner='classType'
            isVisibleToAthletes={!!assignment?.isVisibleToAthletes}
            isLocked={assignment.hasResults}
            onToggleVisibility={workoutAssignment.toggleVisibility}
            onViewWhiteboard={workoutAssignment.viewWhiteboard}
            onEditWorkout={workoutAssignment.editWorkout}
            onChangeWorkout={workoutAssignment.changeWorkout}
            onDeleteWorkout={workoutAssignment.requestDelete}
            target={
              <ActionIcon
                variant='subtle'
                color='green'
                size='lg'
                aria-label='Workout assignment options'
                onClick={e => e.stopPropagation()}
                sx={{
                  height: 'auto',
                  '&:hover': { backgroundColor: theme.colors.green[1] },
                }}
              >
                <IconDotsVertical size={16} />
              </ActionIcon>
            }
          />
        </Flex>
      )}
      <AssignWorkoutModal
        opened={workoutAssignment.assignModalOpened}
        onClose={workoutAssignment.closeAssignModal}
        target={{ kind: 'classType', id: kiloClassTypeId, name: kiloClassTypeName }}
        date={date}
        defaultVisibleToAthletes={assignment?.isVisibleToAthletes ?? true}
      />
      <DeleteWorkoutModal
        opened={workoutAssignment.deleteModalOpened}
        onClose={workoutAssignment.closeDeleteModal}
        onConfirm={workoutAssignment.confirmDelete}
        isLoading={workoutAssignment.deleteLoading}
        workoutId={assignment?.workoutId ?? ''}
        workoutName={assignment?.workoutTemplate?.name}
      />
    </>
  );
};

export default WorkoutAssignedCell;
